import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowDownLeft, ArrowUpRight, CalendarClock, Mail, MessageCircle, Phone, Search, StickyNote, type LucideIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { useCrm2Conversations, type Conversation } from './api'
import { DealSheet } from './DealSheet'
import { Crm2Frame, KIND_LABEL, LINK_CHIP, usePeopleNames, when } from './shared'

const ICON: Record<string, LucideIcon> = { note: StickyNote, email: Mail, call: Phone, meeting: CalendarClock, whatsapp: MessageCircle }
const KINDS = ['', 'email', 'call', 'meeting', 'whatsapp', 'note'] as const

/** Every conversation logged anywhere in TapCRMv2, newest first, with a wide pane to read it in. */
export function ConversationsPage() {
  const { workspaceSlug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState('')
  const kind = params.get('kind') ?? ''
  const mine = params.get('mine') === '1'
  const picked = params.get('c')
  const list = useCrm2Conversations({ kind, q: q.trim(), mine })
  const name = usePeopleNames()
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }
  const rows = list.data ?? []
  const current: Conversation | undefined = rows.find((r) => r.id === picked) ?? rows[0]

  return (
    <Crm2Frame title="Conversations" description="Every email, call, meeting, WhatsApp and note logged against any account, in one place.">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search words, company or person" className="h-9 w-72 pl-8" aria-label="Search conversations" />
        </div>
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Kind">
          {KINDS.map((k) => (
            <button key={k || 'all'} type="button" aria-pressed={kind === k} onClick={() => set('kind', k || null)} className={cn('rounded-md px-3 py-1', kind === k ? 'bg-accent font-medium' : 'text-muted-foreground')}>
              {k ? KIND_LABEL[k] : 'All'}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={mine} onChange={(e) => set('mine', e.target.checked ? '1' : null)} />
          Only my deals and mine
        </label>
      </div>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <LoadingState label="Loading conversations" />
      ) : (
        <div className="grid h-[calc(100vh-15rem)] min-h-[28rem] overflow-hidden rounded-xl border border-border bg-card shadow-sm md:grid-cols-[24rem_minmax(0,1fr)]">
          <ul className="min-h-0 divide-y divide-border-soft overflow-y-auto border-r border-border-soft">
            {rows.map((r) => {
              const Icon = ICON[r.kind] ?? StickyNote
              const on = current?.id === r.id
              return (
                <li key={r.id}>
                  <button type="button" onClick={() => set('c', r.id)} className={cn('grid w-full gap-0.5 px-4 py-3 text-left text-sm', on ? 'bg-accent' : 'hover:bg-accent/50')} aria-current={on}>
                    <span className="flex items-center gap-2">
                      <Icon className="size-4 shrink-0 text-brand" />
                      <span className="truncate font-medium">{r.contactName ?? r.accountName}</span>
                      <span className="ml-auto shrink-0 text-tiny text-text-muted">{when(r.occurredAt)}</span>
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {r.contactName ? `${r.accountName} · ` : ''}
                      {r.subject ?? KIND_LABEL[r.kind]}
                    </span>
                    {r.body ? <span className="line-clamp-1 text-xs text-text-muted">{r.body}</span> : null}
                  </button>
                </li>
              )
            })}
            {!rows.length ? <li className="px-4 py-6 text-center text-sm text-muted-foreground">No conversations match.</li> : null}
          </ul>
          <div className="min-h-0 overflow-y-auto">
            {current ? (
              <article className="grid gap-4 p-6">
                <header className="grid gap-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-foreground">{KIND_LABEL[current.kind]}</span>
                    {current.direction ? (
                      <span className="inline-flex items-center gap-1">
                        {current.direction === 'in' ? <ArrowDownLeft className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
                        {current.direction === 'in' ? 'From them' : 'From us'}
                      </span>
                    ) : null}
                    <span>{when(current.occurredAt)}</span>
                    <span>· logged by {name(current.authorId)}</span>
                  </div>
                  <h2 className="text-xl font-semibold">{current.subject ?? KIND_LABEL[current.kind]}</h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <Link to={paths.crm2Account(workspaceSlug, current.accountId)} className={cn(LINK_CHIP, 'text-sm')}>
                      {current.accountName}
                    </Link>
                    {current.contactId && current.contactName ? (
                      <Link to={paths.crm2Contact(workspaceSlug, current.contactId)} className={cn(LINK_CHIP, 'text-sm')}>
                        {current.contactName}
                        {current.contactEmail ? <span className="text-muted-foreground"> · {current.contactEmail}</span> : null}
                      </Link>
                    ) : null}
                    {current.dealId && current.dealName ? (
                      <button type="button" onClick={() => set('deal', current.dealId)} className={cn(LINK_CHIP, 'text-sm')}>
                        Deal: {current.dealName}
                      </button>
                    ) : null}
                  </div>
                </header>
                {current.body ? <div className="whitespace-pre-wrap rounded-lg border border-border-soft bg-surface p-4 text-sm leading-relaxed">{current.body}</div> : <p className="text-sm text-muted-foreground">No details were written down.</p>}
              </article>
            ) : (
              <p className="p-6 text-sm text-muted-foreground">Pick a conversation to read it.</p>
            )}
          </div>
        </div>
      )}
      <DealSheet id={params.get('deal')} onClose={() => set('deal', null)} onOpen={(id) => set('deal', id)} />
    </Crm2Frame>
  )
}
